import type { SlateBlock, SlateLeaf } from '@atlas/renderer';
import { Range, type Editor } from 'slate';
import type { TIconButtonsGroupItem } from '@atlas/uicorn/lib/Control/IconButtonsGroup';
import type { TEditorFeature } from '@atlas/tool/box/slate/editorFeatures';
import React from 'react';
import FontBoldIcon from '@atlas/uicorn/lib/Icon/FontBoldIcon';
import FontItalicIcon from '@atlas/uicorn/lib/Icon/FontItalicIcon';
import FontUnderlineIcon from '@atlas/uicorn/lib/Icon/FontUnderlineIcon';
import FontStrikethroughIcon from '@atlas/uicorn/lib/Icon/FontStrikethroughIcon';
import CodeIcon from '@atlas/uicorn/lib/Icon/CodeIcon';
import LinkIcon from '@atlas/uicorn/lib/Icon/LinkIcon';
import UnlinkIcon from '@atlas/uicorn/lib/Icon/UnlinkIcon';
import QuoteIcon from '@atlas/uicorn/lib/Icon/QuoteIcon';
import HeadingIcon from '@atlas/uicorn/lib/Icon/HeadingIcon';
import CodeBlockIcon from '@atlas/uicorn/lib/Icon/CodeBlockIcon';
import CalloutIcon from '@atlas/uicorn/lib/Icon/CalloutIcon';
import ListNumberedIcon from '@atlas/uicorn/lib/Icon/ListNumberedIcon';
import ListBulletedIcon from '@atlas/uicorn/lib/Icon/ListBulletedIcon';
import TableIcon from '@atlas/uicorn/lib/Icon/TableIcon';
import LineIcon from '@atlas/uicorn/lib/Icon/LineIcon';
import ImageIcon from '@atlas/uicorn/lib/Icon/ImageIcon';
import EmbedButton from '@atlas/uicorn/lib/RichEditor/embed-button';
import toggleMark from '@atlas/tool/box/slate/toggleMark';
import toggleBlock from '@atlas/tool/box/slate/toggleBlock';
import isBlockActive from '@atlas/tool/box/slate/isBlockActive';
import insertTable from '@atlas/tool/box/slate/insertTable';
import insertHorizontalLine from '@atlas/tool/box/slate/insertHorizontalLine';
import insertImage from '@atlas/tool/box/slate/insertImage';
import {
    blockQuoteHotkey,
    boldHotkey,
    bulletedListHotkey,
    codeBlockHotkey,
    codeHotkey,
    headingOneHotkey,
    headingThreeHotkey,
    headingTwoHotkey,
    italicHotkey,
    linkHotkey,
    numberedListHotkey,
    strikethroughHotkey,
    underlineHotkey,
} from '@atlas/tool/box/slate/handleKeyDownEvent';
import stopMarksEditing from '@atlas/tool/box/slate/stopMarksEditing';
import toggleLink from '@atlas/tool/box/slate/toggleLink';

export default function translateRichInputFeatureToIconButtonGroupItem<
    TProps extends TTranslateRichInputFeatureToIconButtonGroupItemProps,
>(props: TProps, item: TRichInputFeatureItem<TProps>): TIconButtonsGroupItem | null {
    if (item === '|') return { separator: true };

    if (typeof item === 'function') return item(props);

    const { editor, marks, disabled, node } = props;

    // Text features
    if (item === 'bold') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Mark text as bold <kbd>{boldHotkey.toString()}</kbd>
                </>
            ),
            icon: <FontBoldIcon />,
            active: marks?.bold,
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleMark(editor, 'bold'),
        };
    }

    if (item === 'italic') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Mark text as italic <kbd>{italicHotkey.toString()}</kbd>
                </>
            ),
            icon: <FontItalicIcon />,
            active: marks?.italic,
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleMark(editor, 'italic'),
        };
    }

    if (item === 'underline') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Mark text as underline <kbd>{underlineHotkey.toString()}</kbd>
                </>
            ),
            icon: <FontUnderlineIcon />,
            active: marks?.underline,
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleMark(editor, 'underline'),
        };
    }

    if (item === 'strikethrough') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Mark text as strikethrough <kbd>{strikethroughHotkey.toString()}</kbd>
                </>
            ),
            icon: <FontStrikethroughIcon />,
            active: marks?.strikethrough,
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleMark(editor, 'strikethrough'),
        };
    }

    if (item === 'code') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Mark text as code <kbd>{codeHotkey.toString()}</kbd>
                </>
            ),
            icon: <CodeIcon />,
            active: marks?.code,
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleMark(editor, 'code'),
        };
    }

    if (item === 'link') {
        // random() is here to disable image links
        const isImageFocused = node && 'type' in node && node.type === 'image' && !Math.random();
        const isLinkFocused =
            (isImageFocused && typeof node.link === 'string') || (marks && typeof marks.link === 'string');
        // TODO
        return {
            key: item,
            tooltip: () => (
                <>
                    {isImageFocused ? (
                        <>
                            Add link <kbd>{linkHotkey.toString()}</kbd>
                        </>
                    ) : (
                        <>
                            Mark text as link <kbd>{linkHotkey.toString()}</kbd>
                        </>
                    )}
                </>
            ),
            icon: isLinkFocused ? <UnlinkIcon /> : <LinkIcon />,
            active: isLinkFocused,
            disabled:
                disabled ||
                !editor.selection ||
                (!isLinkFocused && !isImageFocused && Range.isCollapsed(editor.selection)),
            tabIndex: -1,
            onClick: () => {
                if (!editor) return;
                toggleLink(editor);
                if (!isLinkFocused) {
                    setTimeout(() => stopMarksEditing(editor, ['link']), 100);
                }
            },
        };
    }

    // Block features
    if (item === 'block-quote') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Insert quote block <kbd>{blockQuoteHotkey.toString()}</kbd>
                </>
            ),
            icon: <QuoteIcon />,
            active: isBlockActive(editor, 'block-quote'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleBlock(editor, 'block-quote'),
        };
    }

    if (item === 'heading-one') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Insert heading <kbd>{headingOneHotkey.toString()}</kbd>
                </>
            ),
            icon: <HeadingIcon level="1" />,
            active: isBlockActive(editor, 'heading-one'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleBlock(editor, 'heading-one'),
        };
    }

    if (item === 'heading-two') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Insert subheading <kbd>{headingTwoHotkey.toString()}</kbd>
                </>
            ),
            icon: <HeadingIcon level="2" />,
            active: isBlockActive(editor, 'heading-two'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleBlock(editor, 'heading-two'),
        };
    }

    if (item === 'heading-three') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Insert subheading <kbd>{headingThreeHotkey.toString()}</kbd>
                </>
            ),
            icon: <HeadingIcon level="3" />,
            active: isBlockActive(editor, 'heading-three'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleBlock(editor, 'heading-three'),
        };
    }

    if (item === 'code-block') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Insert code block <kbd>{codeBlockHotkey.toString()}</kbd>
                </>
            ),
            icon: <CodeBlockIcon />,
            active: isBlockActive(editor, 'code-block'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleBlock(editor, 'code-block'),
        };
    }

    if (item === 'callout') {
        return {
            tooltip: 'Insert callout block',
            icon: <CalloutIcon />,
            active: isBlockActive(editor, 'callout'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleBlock(editor, 'callout'),
        };
    }

    if (item === 'numbered-list') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Insert numbered list <kbd>{numberedListHotkey.toString()}</kbd>
                </>
            ),
            icon: <ListNumberedIcon />,
            active: isBlockActive(editor, 'numbered-list'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleBlock(editor, 'numbered-list'),
        };
    }

    if (item === 'bulleted-list') {
        return {
            key: item,
            tooltip: () => (
                <>
                    Insert bulleted list <kbd>{bulletedListHotkey.toString()}</kbd>
                </>
            ),
            icon: <ListBulletedIcon />,
            active: isBlockActive(editor, 'bulleted-list'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && toggleBlock(editor, 'bulleted-list'),
        };
    }

    if (item === 'table') {
        return {
            tooltip: 'Insert table',
            icon: <TableIcon />,
            active: isBlockActive(editor, 'table'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && insertTable(editor),
        };
    }

    if (item === 'horizontal-line') {
        return {
            tooltip: 'Insert horizontal line',
            icon: <LineIcon />,
            active: isBlockActive(editor, 'horizontal-line'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && insertHorizontalLine(editor),
        };
    }

    // TODO: Support video and video-embed blocks
    if (item === 'image') {
        return {
            tooltip: 'Insert image',
            icon: <ImageIcon />,
            active: isBlockActive(editor, 'image'),
            disabled: disabled,
            tabIndex: -1,
            onClick: () => editor && insertImage(editor),
        };
    }

    if (item === 'embed') {
        return {
            tooltip: 'Insert embed',
            icon: <EmbedButton />,
            active: isBlockActive(editor, 'embed'),
            disabled: disabled,
            tabIndex: -1,
        };
    }

    console.error(`RichInputControl "${item}" handler is not defined`);
    return null;
}

// #region Types

export type TTranslateRichInputFeatureToIconButtonGroupItemProps = {
    editor: Editor;
    marks: Omit<SlateLeaf, 'text'> | undefined;
    disabled?: boolean;
    node?: SlateBlock | SlateLeaf;
};

export type TRichInputFeatureSeparator = '|';

export type TRichInputFeatureCustomItem<
    TProps extends TTranslateRichInputFeatureToIconButtonGroupItemProps = TTranslateRichInputFeatureToIconButtonGroupItemProps,
> = (props: TProps) => TIconButtonsGroupItem | React.ReactElement | null;

export type TRichInputFeatureItem<
    TProps extends TTranslateRichInputFeatureToIconButtonGroupItemProps = TTranslateRichInputFeatureToIconButtonGroupItemProps,
> = Exclude<TEditorFeature, 'video' | 'video-embed'> | TRichInputFeatureSeparator | TRichInputFeatureCustomItem<TProps>;

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('translateRichInputFeatureToIconButtonGroupItem', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
